"use client"

import { useState, useEffect } from "react"
import { Button } from "../../../components/ui/button"
import { Checkbox } from "../../../components/ui/checkbox"
import { Category } from "../../../lib/types"
import { FolderOpen, X, Check } from "lucide-react"

interface ShareAllCategoriesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  categories: Category[] // só as próprias — categorias compartilhadas por outros não entram aqui
  onConfirm: (includedIds: string[]) => void
}

export function ShareAllCategoriesModal({
  open,
  onOpenChange,
  categories,
  onConfirm,
}: ShareAllCategoriesModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      // Categoria sem a flag definida ainda (todas as já existentes) entra
      // marcada por padrão — precisa ser uma ação explícita pra excluir.
      setSelectedIds(
        categories.filter((c) => (c as any).includeInShareAll !== false).map((c) => c.id)
      )
    }
  }, [open, categories])

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleAll = () => {
    setSelectedIds((prev) =>
      prev.length === categories.length ? [] : categories.map((c) => c.id)
    )
  }

  const handleConfirm = () => {
    onConfirm(selectedIds)
    onOpenChange(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <header className="flex items-center justify-between border-b px-6 py-4 w-full gap-4">
        <div>
          <h1 className="text-xl font-semibold">Categorias no link "Compartilhar Todas"</h1>
          <p className="text-sm text-muted-foreground">
            Desmarque as categorias que não devem aparecer pra quem abrir esse link. Uma categoria desmarcada continua acessível se você compartilhar o link dela especificamente.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button variant="outline" onClick={toggleAll}>
            {selectedIds.length === categories.length ? "Desmarcar todas" : "Marcar todas"}
          </Button>
          <Button onClick={handleConfirm}>
            <Check className="h-4 w-4 mr-2" />
            Salvar e Continuar
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        {categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              Nenhuma categoria própria criada ainda.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {categories.map((category) => {
              const isSelected = selectedIds.includes(category.id)
              return (
                <button
                  type="button"
                  key={category.id}
                  onClick={() => toggle(category.id)}
                  className={`group relative rounded-lg border-2 overflow-hidden text-left transition-all ${
                    isSelected
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-transparent hover:border-muted-foreground/30 opacity-60"
                  }`}
                >
                  <div className="relative aspect-video flex items-center justify-center bg-muted">
                    {(category as any).thumbnail ? (
                      <img
                        src={(category as any).thumbnail}
                        alt={category.name}
                        className="h-full w-full object-cover absolute"
                      />
                    ) : (
                      <FolderOpen className="h-8 w-8 text-muted-foreground" />
                    )}
                    <div
                      className={`absolute inset-0 bg-primary/10 transition-opacity ${
                        isSelected ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <div className="absolute top-2 left-2">
                      <Checkbox
                        checked={isSelected}
                        className="h-5 w-5 border-2 bg-background/80 backdrop-blur-sm"
                      />
                    </div>
                  </div>
                  <div className="p-3 bg-card">
                    <p className="font-medium text-sm line-clamp-1">{category.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                      {category.projectIds.length} projeto(s)
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}
